/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  CloudSyncOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { Button, Progress, Spin, Typography, message } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useCopyDataProgressStore } from '@/store/copyDataProgressStore';
import { subscribeCopyDataJob } from '@/utils/copyDataProgressSync';
import { useCopyDataStatusPoll } from '@/hooks/useCopyDataStatusPoll';
import type { CopyDataStepStatus } from '@/types/copy-data-progress';

function StepStatusIcon({ status }: { status: CopyDataStepStatus }) {
  if (status === 'done' || status === 'skipped') {
    return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />;
  }
  if (status === 'failed') {
    return <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />;
  }
  if (status === 'running') {
    return <Spin size="small" />;
  }
  return (
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 border-stone-300 bg-white"
      aria-hidden
    />
  );
}

export const FloatingCopyDataPanel: React.FC = () => {
  const { t } = useTranslation();
  const visible = useCopyDataProgressStore((s) => s.visible);
  const phase = useCopyDataProgressStore((s) => s.phase);
  const steps = useCopyDataProgressStore((s) => s.steps);
  const percent = useCopyDataProgressStore((s) => s.percent);
  const tuNam = useCopyDataProgressStore((s) => s.tuNam);
  const denNam = useCopyDataProgressStore((s) => s.denNam);
  const errorMessage = useCopyDataProgressStore((s) => s.errorMessage);
  const minimized = useCopyDataProgressStore((s) => s.minimized);
  const setMinimized = useCopyDataProgressStore((s) => s.setMinimized);
  const dismiss = useCopyDataProgressStore((s) => s.dismiss);
  const hydrateFromStorage = useCopyDataProgressStore((s) => s.hydrateFromStorage);
  const applyRemoteSnapshot = useCopyDataProgressStore((s) => s.applyRemoteSnapshot);

  const prevPhaseRef = useRef(phase);

  useCopyDataStatusPoll();

  useEffect(() => {
    hydrateFromStorage();
    return subscribeCopyDataJob((snapshot) => {
      applyRemoteSnapshot(snapshot);
    });
  }, [hydrateFromStorage, applyRemoteSnapshot]);

  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (prev === phase) return;
    if (phase === 'complete' && prev === 'running') {
      message.success(
        t('bsc.msg.chuyendulieuthanhcong', 'Chuyển dữ liệu thành công')
      );
    } else if (phase === 'failed' && prev === 'running') {
      message.error(
        errorMessage || t('bsc.msg.chuyendulieuthatbai', 'Chuyển dữ liệu thất bại')
      );
    }
  }, [phase, errorMessage, t]);

  if (!visible || phase === 'idle') return null;

  const title =
    phase === 'complete'
      ? t('bsc.msg.chuyendulieuthanhcong', 'Chuyển dữ liệu thành công')
      : phase === 'failed'
        ? t('bsc.msg.chuyendulieuthatbai', 'Chuyển dữ liệu thất bại')
        : t('bsc.msg.dangchuyendulieu', 'Đang chuyển dữ liệu...');

  const subtitle =
    tuNam != null && denNam != null
      ? t('bsc.msg.copydulieutu', `Từ năm ${tuNam} sang năm ${denNam}`, { tuNam, denNam })
      : '';

  const canDismiss = phase === 'complete' || phase === 'failed';
  const progressStatus =
    phase === 'failed' ? 'exception' : phase === 'complete' ? 'success' : 'active';
  const allCopyStepsFinished =
    phase === 'running' &&
    steps.length > 0 &&
    steps.every((s) => s.status === 'done' || s.status === 'skipped');
  const displayPercent = phase === 'running' ? Math.min(percent, 99) : percent;
  const hasStepInProgress = steps.some(
    (s) => s.status === 'running' || s.status === 'done' || s.status === 'failed'
  );
  const isStarting =
    phase === 'running' && !allCopyStepsFinished && displayPercent === 0 && !hasStepInProgress;

  if (minimized) {
    return (
      <button
        type="button"
        className={classNames(
          'fixed z-[210] flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-2',
          'shadow-[0_10px_40px_-4px_rgba(12,40,68,0.14)]',
          'bottom-4 right-4',
          'text-left text-[13px] font-medium text-[#1B252E] hover:bg-blue-50'
        )}
        onClick={() => setMinimized(false)}
        aria-label={title}
      >
        {phase === 'running' ? (
          <Spin size="small" />
        ) : phase === 'complete' ? (
          <CheckCircleFilled style={{ color: '#52c41a' }} />
        ) : (
          <CloseCircleFilled style={{ color: '#ff4d4f' }} />
        )}
        <span className="max-w-[180px] truncate">
          {isStarting
            ? t('bsc.msg.dangchuanbisaochep', 'Đang chuẩn bị sao chép dữ liệu...')
            : title}
        </span>
        <span className="text-[#637381]">{displayPercent}%</span>
      </button>
    );
  }

  return (
    <div
      className={classNames(
        'fixed z-[210] w-[min(100vw-1.25rem,380px)] overflow-hidden rounded-xl',
        'border border-gray-200/90 bg-white',
        'shadow-[0_10px_40px_-4px_rgba(12,40,68,0.14),0_4px_16px_-4px_rgba(12,40,68,0.08)]',
        'bottom-4 right-4'
      )}
      role="complementary"
      aria-label={title}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100/90 bg-gradient-to-r from-[#f8fafc] via-white to-[#f4f8fc] px-4 py-3">
        <div className="min-w-0 flex items-start gap-2.5">
          {phase === 'running' ? (
            <Spin size="small" className="mt-1" />
          ) : (
            <CloudSyncOutlined style={{ fontSize: 22, color: '#1677ff', marginTop: 2 }} />
          )}
          <div className="min-w-0">
            <div className="text-[14px] font-semibold tracking-tight text-[#1B252E]">{title}</div>
            {subtitle ? (
              <Typography.Text type="secondary" className="text-[12px]">
                {subtitle}
              </Typography.Text>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={() => setMinimized(true)}
            aria-label={t('bsc.btn.thugon', 'Thu gọn')}
          />
          {canDismiss ? (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => dismiss()}
              aria-label={t('bsc.btn.dong', 'Đóng')}
            />
          ) : null}
        </div>
      </div>

      <div className="px-4 pt-3 pb-2">
        <Progress percent={displayPercent} status={progressStatus} size="small" />
        {isStarting ? (
          <div className="mt-2 text-[12px] text-[#1677ff]">
            {t('bsc.msg.dangchuanbisaochep', 'Đang chuẩn bị sao chép dữ liệu...')}
          </div>
        ) : null}
        {allCopyStepsFinished ? (
          <div className="mt-2 text-[12px] text-[#1677ff]">
            {t(
              'bsc.msg.danghoantatcopydata',
              'Đang hoàn tất (kích hoạt năm đích)...'
            )}
          </div>
        ) : null}
        {errorMessage && phase === 'failed' ? (
          <Typography.Text type="danger" className="mt-1 block text-[12px]">
            {errorMessage}
          </Typography.Text>
        ) : null}
      </div>

      <ul className="max-h-[min(50vh,280px)] list-none space-y-0 overflow-auto px-2 pb-3 m-0">
        {steps.map((step) => (
          <li
            key={step.stepKey}
            className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-[#1B252E] hover:bg-[rgba(12,104,233,0.04)]"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <StepStatusIcon status={step.status} />
            </span>
            <span
              className={classNames(
                'min-w-0 flex-1 leading-snug',
                step.status === 'pending' && 'text-[#637381]',
                step.status === 'running' && 'font-medium text-[#1677ff]',
                step.status === 'failed' && 'text-[#ff4d4f]'
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ul>

      {canDismiss ? (
        <div className="border-t border-gray-100 px-4 py-2.5 text-right">
          <Button type="primary" size="small" onClick={() => dismiss()}>
            {t('bsc.btn.dong', 'Đóng')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
